import React from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Switch
} from '@mui/material'
import { snackBarVisibleVar } from 'context'
import { IModalProps } from 'hooks'
import { startCase } from 'lodash-es'
import { useUiStatesQuery, UiStateName, useUpsertUiStateMutation } from 'types'

const AVAILABLE_UI_STATES = [
  UiStateName.UnifiedOnboardingStarted,
  UiStateName.EligibleForNetPromoterScoreSurvey,
  UiStateName.NetPromoterScoreSurveyVisited,
  UiStateName.EligibleForProductMarketFitSurvey,
  UiStateName.ProductMarketFitSurveyVisited,
  UiStateName.NoraHomeTutorialSeen
]

export const ManageUiStatesDialog = ({ closeModal, modalState }: IModalProps) => {
  const { data, loading } = useUiStatesQuery()

  const uiStates = data?.uiStates || []
  const uiStateMap = Object.fromEntries(uiStates.map((uiState) => [uiState.name, uiState.value]))

  const [upsertUiState] = useUpsertUiStateMutation()

  const handleToggle =
    (name: UiStateName) => async (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked
      await upsertUiState({
        variables: { name, value: newValue },
        optimisticResponse: {
          upsertUiState: {
            __typename: 'PrivateUIState',
            name,
            value: newValue
          }
        },
        onError: (error) => snackBarVisibleVar({ open: true, message: error.message }),
        update: (cache, { data: mutationData }) => {
          if (!mutationData?.upsertUiState) {
            return
          }

          cache.modify({
            fields: {
              uiStates(existing = []) {
                const updated = existing.map((uiState: any) =>
                  uiState.name === name ? { ...uiState, value: newValue } : uiState
                )
                // If not found, add it
                if (!updated.find((uiState: any) => uiState.name === name)) {
                  updated.push({ __typename: 'UIState', name, value: newValue })
                }
                return updated
              }
            }
          })
        }
      })
    }

  return (
    <Dialog maxWidth="xs" fullWidth open={modalState} onClose={closeModal}>
      <DialogTitle>Manage UI States</DialogTitle>
      <DialogContent>
        {AVAILABLE_UI_STATES.map((name) => (
          <FormControl key={name} fullWidth margin="dense">
            <FormControlLabel
              disabled={loading}
              control={
                <Switch
                  checked={!!uiStateMap[name]}
                  onChange={handleToggle(name)}
                  name={name}
                  disabled={loading}
                />
              }
              label={startCase(name)}
            />
          </FormControl>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
